/**
 * Every word on the landing page, in both languages.
 *
 * Kept out of the component for the same reason the legal documents are: copy
 * gets rewritten far more often than layout, and a translator (or the owner)
 * should be able to change a sentence without reading JSX.
 *
 * Two rules this file holds itself to:
 *
 * 1. Every number here is real. The Café Nube figures come from that account's
 *    own records, and it is labelled a demo everywhere it appears.
 * 2. The example businesses are illustrations and say so. There are no
 *    testimonials, because there are no customers yet — inventing them would
 *    be a lie told to someone about to spend money, and the first person to
 *    check would find nothing behind the name.
 */

export type Copy = { es: string; en: string };

export const HERO = {
  title: {
    es: 'Sabes cuánto vendiste.\nAhora sabrás si vas bien.',
    en: 'You know what you sold.\nNow know if you are doing well.',
  },
  sub: {
    es: 'Quantifyr lleva las ventas, los clientes, los costos y el inventario de tu negocio, y cada semana te dice en español claro qué está cambiando, por qué, y qué hacer al respecto.',
    en: 'Quantifyr keeps your sales, customers, costs and stock, and each week tells you in plain words what is changing, why, and what to do about it.',
  },
  cta: { es: 'Crear mi cuenta gratis', en: 'Create my free account' },
  ctaSignedIn: { es: 'Ir a mi panel', en: 'Go to my dashboard' },
  note: { es: 'Empiezas gratis. Sin tarjeta.', en: 'Start free. No card.' },
};

export const PROBLEM = {
  eyebrow: { es: 'El problema', en: 'The problem' },
  title: {
    es: 'El corte de caja te dice cuánto entró. No te dice si eso es bueno.',
    en: 'Your till tells you what came in. It does not tell you whether that is good.',
  },
  lede: {
    es: 'Casi todos los negocios pequeños tienen los datos — en la terminal, en un cuaderno, en la cabeza del dueño. Lo que no tienen es una hora a la semana para sentarse a compararlos.',
    en: 'Almost every small business has the data — in the card reader, in a notebook, in the owner’s head. What it does not have is an hour a week to sit down and compare it.',
  },
  points: [
    {
      title: { es: 'Te enteras tarde', en: 'You find out late' },
      body: {
        es: 'Cuando notas que el mes fue malo, el mes ya pasó. La caída empezó tres semanas antes y nadie la vio.',
        en: 'By the time you notice the month was bad, the month is over. The slide started three weeks earlier and nobody saw it.',
      },
    },
    {
      title: { es: 'No sabes qué lo movió', en: 'You cannot tell what moved it' },
      body: {
        es: 'Vendiste menos. ¿Vino menos gente, o la misma gente gastó menos? Son dos problemas distintos y se arreglan distinto.',
        en: 'You sold less. Did fewer people come, or did the same people spend less? Those are two different problems with two different fixes.',
      },
    },
    {
      title: { es: 'El inventario avisa cuando ya no hay', en: 'Stock tells you once it is gone' },
      body: {
        es: 'Te quedas sin leche un sábado. O compras de más y se echa a perder. Las dos cosas cuestan dinero.',
        en: 'You run out of milk on a Saturday. Or you over-buy and it spoils. Both cost money.',
      },
    },
  ],
};

export const STEPS = {
  eyebrow: { es: 'Cómo funciona', en: 'How it works' },
  title: {
    es: 'Tres pasos, y el primero toma un minuto a la semana.',
    en: 'Three steps, and the first takes a minute a week.',
  },
  items: [
    {
      title: { es: 'Registras, o lo conectas', en: 'Record it, or connect it' },
      body: {
        es: 'Escribes cuánto vendiste, cuánta gente vino y cuánto gastaste — por semana o día por día. O conectas Mercado Pago, Clip o Shopify y tus ventas entran solas cada día.',
        en: 'Type what you sold, how many came and what you spent — weekly or day by day. Or connect Mercado Pago, Clip or Shopify and your sales arrive on their own.',
      },
    },
    {
      title: { es: 'La app compara', en: 'The app compares' },
      body: {
        es: 'Contra tu semana pasada, contra el mismo día de la semana anterior, contra tu meta. Si la semana va a la mitad compara solo los días que ya cerraron: no te dice que vas mal porque todavía no acaba el viernes.',
        en: 'Against last week, against the same weekday a week back, against your goal. Mid-week it only counts the days that closed — it will not call you down because Friday has not happened yet.',
      },
    },
    {
      title: { es: 'Te dice qué hacer', en: 'It tells you what to do' },
      body: {
        es: 'No nada más qué pasó. Qué lo causó, cuánto dinero representa, y dos o tres acciones concretas para esta semana.',
        en: 'Not just what happened. What caused it, what it is worth in money, and two or three concrete things to do this week.',
      },
    },
  ],
};

export interface Feature {
  eyebrow: Copy;
  title: Copy;
  body: Copy[];
  shot: string;
  shotAlt: Copy;
  caption?: Copy;
  reverse?: boolean;
}

export const FEATURES: Feature[] = [
  {
    eyebrow: { es: 'El tablero', en: 'The dashboard' },
    title: {
      es: 'Cuatro números arriba de todo, antes de cualquier gráfica.',
      en: 'Four numbers above everything, before any chart.',
    },
    body: [
      {
        es: 'Si estás creciendo, cuánto te queda de cada peso, si entra más de lo que sale, y cuánto necesitas vender esta semana para cubrir tus costos. Cada uno con una etiqueta que dice si está bien o mal, para que no tengas que interpretarlo.',
        en: 'Whether you are growing, how much of each peso you keep, whether more comes in than goes out, and what you need to sell this week to cover costs. Each with a label saying whether that is good or bad, so nothing needs interpreting.',
      },
      {
        es: 'Debajo, tu meta de la semana con lo que llevas. Y cuando algo va mal, el mismo espacio lo dice igual de claro: el número se pone naranja.',
        en: 'Below it, this week’s goal against what you have. And when something is wrong the same space says so just as plainly: the number turns orange.',
      },
    ],
    shot: '/marketing/dashboard.webp',
    shotAlt: {
      es: 'Tablero de Quantifyr con crecimiento de ventas, margen, flujo de caja y meta semanal',
      en: 'Quantifyr dashboard showing sales growth, margin, cash flow and weekly goal',
    },
  },
  {
    eyebrow: { es: 'Salud del negocio', en: 'Business health' },
    title: {
      es: 'Una calificación, y los factores que la sostienen.',
      en: 'One score, and the factors holding it up.',
    },
    body: [
      {
        es: 'Un número del 0 al 100 que resume cómo vas, con los tres factores que lo explican listados debajo — no es una caja negra. Al lado, las cinco métricas que de verdad mueven un negocio pequeño y la curva de tus últimas doce semanas.',
        en: 'A number from 0 to 100 summing up how you are doing, with the three factors behind it listed underneath — it is not a black box. Beside it, the five metrics that actually move a small business and your last twelve weeks as a curve.',
      },
    ],
    shot: '/marketing/health.webp',
    shotAlt: {
      es: 'Calificación de salud del negocio con factores clave y métricas principales',
      en: 'Business health score with key factors and core metrics',
    },
    reverse: true,
  },
  {
    eyebrow: { es: 'Los análisis', en: 'The analysis' },
    title: {
      es: 'La inteligencia artificial no adivina tu problema. Solo lo explica.',
      en: 'The AI does not guess your problem. It only explains it.',
    },
    body: [
      {
        es: 'Esto vale la pena decirlo claro. Quién decide qué está pasando con tu negocio son reglas fijas que miran tus números: cuánto cambió, qué lo movió, qué tan grave es. El modelo de lenguaje entra hasta el final y su único trabajo es escribirlo en español que se entienda.',
        en: 'This is worth saying plainly. What decides what is happening to your business is fixed rules reading your figures: how much changed, what moved it, how serious it is. The language model comes in at the end, and its only job is to say it in words you understand.',
      },
      {
        es: 'Por eso dos negocios con los mismos números reciben el mismo diagnóstico. La IA redacta; no opina.',
        en: 'That is why two businesses with the same figures get the same diagnosis. The AI writes; it does not have opinions.',
      },
      {
        es: 'La semana se desarma en sus dos cuentas — clientes por gasto promedio — con el eslabón débil señalado. Si entró menos gente, te lo dice, y te advierte que una promoción sobre el ticket no lo va a arreglar.',
        en: 'The week is taken apart into its two sums — customers times average spend — with the weak link marked. If fewer people came it says so, and warns you that a discount on the ticket will not fix it.',
      },
    ],
    shot: '/marketing/insights.webp',
    shotAlt: {
      es: 'Desglose de la semana en clientes por gasto promedio con el eslabón débil marcado',
      en: 'A week broken into customers times average spend with the weak link marked',
    },
  },
  {
    eyebrow: { es: 'Qué hacer', en: 'What to do' },
    title: {
      es: 'Termina donde debe terminar un diagnóstico: en acciones.',
      en: 'It ends where a diagnosis should: in things to do.',
    },
    body: [
      {
        es: 'Tres ideas concretas para esta semana, con por qué cada una tiene sentido para tu caso. Y si quieres preguntar algo específico, puedes: las respuestas se construyen solo con las semanas que registraste, sin promedios de industria ni estimaciones de fuera.',
        en: 'Three concrete ideas for this week, each with why it makes sense for you. And if you want to ask something specific you can: answers are built only from the weeks you recorded, with no industry averages and no outside guesses.',
      },
    ],
    shot: '/marketing/actions.webp',
    shotAlt: {
      es: 'Tres acciones sugeridas para la semana',
      en: 'Three suggested actions for the week',
    },
    reverse: true,
  },
  {
    eyebrow: { es: 'Inventario', en: 'Inventory' },
    title: { es: 'Qué se te acaba primero.', en: 'What runs out first.' },
    body: [
      {
        es: 'No es una lista de existencias: es una respuesta. La app aprende a qué ritmo consumes cada insumo y te dice cuántos días te quedan de cada uno, ordenados por urgencia y con la fecha en que se acaba.',
        en: 'Not a stock list: an answer. The app learns how fast you get through each item and tells you how many days you have left of each, ordered by urgency with the date it runs out.',
      },
      {
        es: 'Y contar es un modo aparte, pensado para la bodega: un insumo a la vez, con lo que tenías la última vez a la vista, y un botón para pasar al siguiente. Van ordenados por lo que se acaba primero, así que si te interrumpe un cliente a la mitad, lo que alcanzaste a contar fue lo que urgía.',
        en: 'And counting is its own mode, built for a storeroom: one item at a time, last time’s number in view, and a button for the next one. They come in the order they will run out, so if a customer interrupts you halfway, the half you finished is the half that mattered.',
      },
    ],
    shot: '/marketing/inventory.webp',
    shotAlt: {
      es: 'Días de inventario restantes por insumo, ordenados por urgencia',
      en: 'Days of stock left per item, ordered by urgency',
    },
  },
];

export const PHONE = {
  eyebrow: { es: 'En el teléfono', en: 'On your phone' },
  title: {
    es: 'Diseñada para el aparato donde de verdad la vas a abrir.',
    en: 'Built for the device you will actually open it on.',
  },
  lede: {
    es: 'No es la versión de computadora encogida. El número de la semana ocupa la pantalla, las tablas se vuelven listas, la navegación baja a una barra que alcanzas con el pulgar, y contar inventario ocupa la pantalla completa. No hay que instalar nada: se abre en el navegador de tu celular.',
    en: 'Not the desktop shrunk. The week’s number owns the screen, tables become lists, navigation drops to a bar your thumb can reach, and counting stock takes the whole display. Nothing to install: it opens in your phone’s browser.',
  },
  // `cap` is printed under the image; `alt` is read instead of it. They are
  // written separately because a caption leans on the picture beside it and
  // alt text has to stand alone.
  shots: [
    {
      src: '/marketing/mobile-dashboard.webp',
      cap: { es: 'El tablero.', en: 'The dashboard.' },
      alt: {
        es: 'Quantifyr en un teléfono: el año en un mapa de calor semanal, con ventas, clientes y ganancia de la semana seleccionada',
        en: 'Quantifyr on a phone: the year as a weekly heatmap, with the selected week’s sales, customers and profit',
      },
    },
    {
      src: '/marketing/mobile-inventory.webp',
      cap: {
        es: 'Lo que se acaba esta semana, con nombre y días.',
        en: 'What runs out this week, by name and days.',
      },
      alt: {
        es: 'Inventario en un teléfono: aviso de diez insumos por acabarse en dos semanas, con sus nombres y días restantes',
        en: 'Inventory on a phone: a warning that ten items run out within two weeks, with their names and days left',
      },
    },
    {
      src: '/marketing/mobile-count.webp',
      cap: {
        es: 'Contar: un insumo, un número.',
        en: 'Counting: one item, one number.',
      },
      alt: {
        es: 'Modo de conteo a pantalla completa: un insumo, cuánto había la última vez, y un campo para el número de hoy',
        en: 'Full-screen counting mode: one item, what there was last time, and a field for today’s number',
      },
    },
  ],
};

export const DEMO = {
  eyebrow: { es: 'Una demostración', en: 'A demonstration' },
  title: {
    es: 'Tres meses de una cafetería, dentro de la app.',
    en: 'Three months of a coffee shop, inside the app.',
  },
  lede: {
    es: 'Café Nube es la cuenta con la que probamos todo. No es un cliente: es una cafetería de demostración con catorce semanas de operación registradas día por día y doce insumos en inventario. Lo decimos porque los números de abajo salen de ahí, y preferimos que sepas de dónde vienen.',
    en: 'Café Nube is the account we test everything on. It is not a customer: it is a demonstration coffee shop with fourteen weeks of trading recorded day by day and twelve items in stock. We say so because the figures below come from it, and you should know where they came from.',
  },
  stats: [
    { n: '96', l: { es: 'días registrados', en: 'days recorded' } },
    { n: '6,142', l: { es: 'clientes atendidos', en: 'customers served' } },
    { n: '$74.70', l: { es: 'ticket promedio', en: 'average ticket' } },
    { n: '12', l: { es: 'insumos vigilados', en: 'items watched' } },
  ],
  closing: {
    es: 'En sus últimas cinco semanas el ticket promedio pasó de $78 a $87 y las ventas semanales de $36,780 a $47,194. Ese es exactamente el movimiento que no se ve en un corte de caja diario, y que la app señala la semana en que empieza.',
    en: 'Over its last five weeks the average ticket went from $78 to $87 and weekly sales from $36,780 to $47,194. That is exactly the movement a daily till count hides, and the one the app flags the week it starts.',
  },
};

/**
 * Illustrations, not customers.
 *
 * Each of these is a sketch of how the same screens read for a different
 * trade — what the owner records, and what the app watches. No figures are
 * attributed to them, because no such business has used the product.
 */
export const EXAMPLES = {
  eyebrow: { es: 'Para quién es', en: 'Who it is for' },
  title: {
    es: 'Si vendes algo y compras algo, sirve.',
    en: 'If you sell something and buy something, it works.',
  },
  lede: {
    es: 'Ejemplos ilustrativos de cómo se acomoda a distintos giros. Ninguno es un cliente real — todavía no los hay, y cuando los haya lo vas a leer aquí con su nombre.',
    en: 'Illustrations of how it fits different trades. None is a real customer — there are none yet, and when there are you will read their names here.',
  },
  items: [
    {
      trade: { es: 'Cafetería', en: 'Coffee shop' },
      tracks: {
        es: 'Ventas del día, cuántos cafés salieron, leche y grano por kilo.',
        en: 'Daily sales, how many coffees went out, milk and beans by the kilo.',
      },
      watches: {
        es: 'Que el ticket promedio no se caiga y que la leche no se acabe en sábado.',
        en: 'That the average ticket holds and the milk does not run out on a Saturday.',
      },
    },
    {
      trade: { es: 'Tienda de abarrotes', en: 'Corner shop' },
      tracks: {
        es: 'Corte diario, número de tickets, lo que repones cada semana.',
        en: 'Daily takings, ticket count, what you restock each week.',
      },
      watches: {
        es: 'Qué días entran más clientes y qué producto se mueve más rápido de lo que compras.',
        en: 'Which days bring more people, and what sells faster than you buy it.',
      },
    },
    {
      trade: { es: 'Estética o barbería', en: 'Salon or barber' },
      tracks: {
        es: 'Servicios del día, cuántas personas atendiste, tinte y material.',
        en: 'Services done, how many people you saw, colour and supplies.',
      },
      watches: {
        es: 'Si vuelven los mismos clientes y si el gasto por visita está subiendo.',
        en: 'Whether the same clients come back, and whether spend per visit is rising.',
      },
    },
    {
      trade: { es: 'Taller o servicio a domicilio', en: 'Workshop or callout trade' },
      tracks: {
        es: 'Trabajos cerrados, cuánto cobraste, refacciones y combustible.',
        en: 'Jobs closed, what you charged, parts and fuel.',
      },
      watches: {
        es: 'Que el costo por trabajo no se coma el margen sin que te des cuenta.',
        en: 'That cost per job is not quietly eating the margin.',
      },
    },
  ],
};

export const EARLY = {
  eyebrow: { es: 'Apenas empezamos', en: 'We are just starting' },
  title: {
    es: 'Sé de los primeros, y que se note.',
    en: 'Be one of the first, and get something for it.',
  },
  body: {
    es: 'Quantifyr es nuevo. No vamos a poner reseñas inventadas para aparentar lo contrario — si estás leyendo esto, eres de los primeros en verlo. A cambio: el precio con el que entres es el que conservas mientras sigas suscrito, y quien te atiende cuando escribes es quien construyó la app.',
    en: 'Quantifyr is new. We are not going to fake reviews to pretend otherwise — if you are reading this, you are among the first. In exchange: the price you join at is the price you keep while you stay, and whoever answers when you write is the person who built it.',
  },
  points: [
    {
      es: 'El precio que tomes hoy no sube mientras sigas suscrito.',
      en: 'The price you take today does not rise while you stay subscribed.',
    },
    {
      es: 'Escribes y contesta quien hizo la app, no una mesa de ayuda.',
      en: 'You write and the person who built it answers, not a help desk.',
    },
    {
      es: 'Lo que pidas pesa: todavía cabe en el camino del producto.',
      en: 'What you ask for counts: it still fits in the product’s path.',
    },
  ],
};

export const PRICING = {
  eyebrow: { es: 'Precio', en: 'Pricing' },
  title: {
    es: 'Empiezas gratis. Pagas cuando te sirva.',
    en: 'Start free. Pay when it earns it.',
  },
  free: {
    name: { es: 'Gratis', en: 'Free' },
    price: '$0',
    items: [
      { on: true, t: { es: 'Registras ventas, clientes y costos', en: 'Record sales, customers and costs' } },
      { on: true, t: { es: 'Tablero con tus tendencias', en: 'Dashboard with your trends' } },
      { on: true, t: { es: 'Tu meta semanal', en: 'Your weekly goal' } },
      { on: false, t: { es: 'Sin análisis con IA ni inventario', en: 'No AI analysis, no inventory' } },
    ],
  },
  paid: {
    name: { es: 'Completo', en: 'Full' },
    price: '$33.33',
    per: { es: 'USD / mes', en: 'USD / month' },
    items: [
      { on: true, t: { es: 'Todo lo de la versión gratis', en: 'Everything in Free' } },
      { on: true, t: { es: 'Análisis semanales con IA y acciones concretas', en: 'Weekly AI analysis and concrete actions' } },
      { on: true, t: { es: 'Inventario con días restantes y modo de conteo', en: 'Inventory with days left and counting mode' } },
      { on: true, t: { es: 'Conexión con Mercado Pago, Clip y Shopify', en: 'Mercado Pago, Clip and Shopify connections' } },
      { on: true, t: { es: 'Comparaciones de largo plazo', en: 'Long-run comparisons' } },
    ],
  },
  note: {
    es: 'Cancelas cuando quieras desde Ajustes, sin llamadas y sin trámites, y conservas el acceso hasta que termine el mes que ya pagaste.',
    en: 'Cancel any time from Settings, no calls and no paperwork, and keep access until the month you already paid for ends.',
  },
};

export const PRIVACY_BLOCK = {
  eyebrow: { es: 'Tus datos', en: 'Your data' },
  title: {
    es: 'Los números de tu negocio son tuyos.',
    en: 'Your business figures are yours.',
  },
  points: [
    {
      title: { es: 'Aislados por cuenta', en: 'Isolated per account' },
      body: {
        es: 'Cada cuenta solo puede leer lo suyo, y esa regla vive en la base de datos, no nada más en la app.',
        en: 'Each account reads only its own, and that rule lives in the database, not just in the app.',
      },
    },
    {
      title: { es: 'Credenciales cifradas', en: 'Encrypted credentials' },
      body: {
        es: 'Los permisos de tus servicios de cobro se guardan cifrados y la app nunca los vuelve a mostrar completos.',
        en: 'Your payment-service permissions are stored encrypted and never shown in full again.',
      },
    },
    {
      title: { es: 'No se venden ni entrenan modelos', en: 'Not sold, not training data' },
      body: {
        es: 'Tus cifras no se venden, no se rentan y no se usan para entrenar inteligencia artificial. Puedes borrar tu cuenta cuando quieras.',
        en: 'Your figures are not sold, not rented and not used to train AI. You can delete your account whenever you want.',
      },
    },
  ],
  link: {
    es: 'Todo esto está escrito con nombre y apellido en el aviso de privacidad, incluido qué se le manda al modelo de lenguaje cuando pides un análisis.',
    en: 'All of it is written out in the privacy notice, including what goes to the language model when you ask for an analysis.',
  },
};

export const CLOSE = {
  title: {
    es: 'Deja de adivinar si el mes fue bueno.',
    en: 'Stop guessing whether the month was good.',
  },
  lede: {
    es: 'Crea tu cuenta, registra tu primera semana y mira qué te dice. Toma menos tiempo que cerrar la caja.',
    en: 'Create your account, record your first week and see what it says. It takes less time than closing the till.',
  },
};

export const FAQ_TITLE = {
  eyebrow: { es: 'Preguntas', en: 'Questions' },
  title: { es: 'Lo que la gente pregunta antes de empezar.', en: 'What people ask before starting.' },
  more: { es: 'Ver todas las preguntas', en: 'See all questions' },
};
